import { useEffect, useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AppShell } from '@/components/layout/AppShell';
import { captureException } from '@/lib/sentry';
import { API_URL } from '@/lib/api';

/**
 * RouteErrorFallback — единый fallback для TanStack Router `errorComponent`.
 *
 * Закрывает Nielsen heuristic 9 (Help users recognize/recover from errors)
 * на route-уровне. Без него любой uncaught error в route-компоненте даёт
 * white-screen на весь viewport — пользователь теряет escape-pattern.
 *
 * Поведение:
 * - Sentry capture при mount (для post-mortem)
 * - friendly UI с понятным сообщением
 * - 2 действия: «Попробовать снова» (router reset, может починить transient),
 *   «На главную» (escape к /)
 * - Dev-mode: collapse с full stack trace (через <details>)
 *
 * Use:
 *   const rootRoute = createRootRoute({
 *     component: () => <Outlet />,
 *     errorComponent: RouteErrorFallback,
 *   });
 */

export interface RouteErrorFallbackProps {
  error: Error;
  reset: () => void;
  /** Optional дополнительный info для Sentry context (route path, etc.). */
  info?: { componentStack?: string };
}

export function RouteErrorFallback({ error, reset, info }: RouteErrorFallbackProps) {
  const router = useRouter();

  // Sentry — post-mortem evidence; повторный crash при unmount не страшен,
  // т.к. captureException безопасен в strict mode.
  useEffect(() => {
    captureException(error, {
      tags: { source: 'RouteErrorFallback' },
      extra: {
        componentStack: info?.componentStack,
        location: typeof window !== 'undefined' ? window.location.pathname : 'ssr',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    // router.invalidate() сбрасывает loader cache + reset() убирает error state
    router.invalidate();
    reset();
  };

  const isDev = import.meta.env.DEV;

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle
              className="h-6 w-6 shrink-0 text-destructive"
              aria-hidden
            />
            <div className="space-y-1.5 min-w-0">
              <h1 className="text-lg font-semibold text-foreground">
                Что-то пошло не так
              </h1>
              <p className="text-sm text-muted-foreground break-words">
                {error.message || 'Неожиданная ошибка в приложении'}
              </p>
            </div>
          </div>

          {isDev && error.stack && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Stack trace (dev only)
              </summary>
              <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-2 text-foreground/80 whitespace-pre-wrap">
                {error.stack}
              </pre>
            </details>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row">
            <Button onClick={handleRetry} className="sm:flex-1">
              Попробовать снова
            </Button>
            <Button asChild variant="outline" className="sm:flex-1">
              <Link to="/">На главную</Link>
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground pt-1">
            Ошибка автоматически отправлена в систему мониторинга
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * NotFoundContent — презентационная 404-карточка (без AppShell, без Sentry).
 * Вынесена отдельно, чтобы её можно было рендерить как внутри shell
 * (notFoundComponent), так и тестировать изолированно.
 *
 * `min-h-full` (а НЕ `min-h-screen`) — чтобы карточка центрировалась внутри
 * `<main>` AppShell'а, не выталкивая sidebar/topbar за viewport (AK-294).
 */
export function NotFoundContent({ guest = false }: { guest?: boolean } = {}) {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle
              className="h-6 w-6 shrink-0 text-muted-foreground"
              aria-hidden
            />
            <div className="space-y-1.5 min-w-0">
              <h1 className="text-lg font-semibold text-foreground">
                Страница не найдена
              </h1>
              <p className="text-sm text-muted-foreground">
                По этому адресу ничего нет. Возможно, страница была перемещена
                или вы перешли по неверной ссылке.
              </p>
            </div>
          </div>

          {/* Гостю «На главную» бесполезна: индекс всё равно упрётся в
              авторизацию и вернёт его на вход — только молча. Говорим прямо. */}
          {guest ? (
            <Button
              className="w-full"
              onClick={() => window.location.replace('/login')}
            >
              Перейти ко входу
            </Button>
          ) : (
            <Button asChild className="w-full">
              <Link to="/">На главную</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * NotFoundFallback — 404 для TanStack Router `notFoundComponent`.
 *
 * AK-294 «Мёртвая навигация»: авторизованному 404 рендерится ВНУТРИ AppShell,
 * поэтому sidebar + topbar остаются на месте — и для несуществующих роутов, и
 * при прямом вводе битого URL.
 *
 * Гостю — тот же текст, но БЕЗ shell. Прежний комментарий здесь утверждал, что
 * «AppShell сам редиректит неавторизованного на /login, так что оборачивать
 * безопасно». Безопасно для кода — не для человека: любой 404 у гостя молча
 * превращался в экран входа, и вместе с ним исчезала причина, по которой он
 * сюда попал. Прод 21.08.2026: приглашённый трижды получил `/login` вместо
 * «приглашение уже использовано» и «письмо отправлено» — оба адреса были
 * мёртвыми маршрутами, а 404 у них отобрал последний шанс объясниться. Класс
 * дефекта шире одного инцидента: диагностическая страница для неавторизованного
 * была невидима by design.
 *
 * Признак гостя спрашиваем сами, голым fetch мимо `api.ts`: тот на 401 уводит
 * на `/login` — ровно то, что мы здесь и лечим.
 */
export function NotFoundFallback() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/v1/auth/me`, { credentials: 'include' })
      .then((r) => {
        if (!cancelled) setAuthed(r.ok);
      })
      .catch(() => {
        // Сеть легла — считаем гостем: standalone-404 работает всегда, а
        // AppShell без /auth/me показал бы бесконечное «Загружаем…».
        if (!cancelled) setAuthed(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
        <p className="text-sm text-muted-foreground">Загружаем…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
        <NotFoundContent guest />
      </div>
    );
  }

  return (
    <AppShell>
      <NotFoundContent />
    </AppShell>
  );
}
